import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Terms = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        <div className="absolute inset-0 bg-[url('/images/hero-brand-image.png')] bg-cover bg-center opacity-5" />
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-secondary via-primary to-accent bg-clip-text text-transparent leading-tight">
              Terms & Conditions
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional tax preparation services agreement
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            
            <Card>
              <CardHeader>
                <CardTitle>Acceptance of Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  By engaging Tiny Tax Lady (Diane Hardesty) for tax preparation services, you agree to be bound by these Terms and Conditions. Please read them carefully before proceeding with our services.
                </p>
                <p>
                  Last Updated: January 2025
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Services Provided</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Tiny Tax Lady specializes in the preparation and filing of federal and state income tax returns for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Individual tax returns (Form 1040 and related schedules)</li>
                  <li>Sole proprietor business returns (Schedule C)</li>
                  <li>Amended tax returns (Form 1040-X)</li>
                  <li>Year-round tax support and consultation</li>
                </ul>
                <p className="font-semibold">
                  Services NOT Provided: We do not prepare tax returns for partnerships, corporations, trusts, estates, or complex business entities.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Client Responsibilities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  As a client, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide complete, accurate, and timely documentation (W-2s, 1099s, receipts, etc.)</li>
                  <li>Disclose all sources of income and relevant tax information</li>
                  <li>Review your completed tax return carefully before authorization to file</li>
                  <li>Maintain copies of all tax documents and returns for at least 7 years</li>
                  <li>Notify us immediately of any errors or omissions discovered after filing</li>
                  <li>Respond to IRS or state notices promptly and forward them to us if assistance is needed</li>
                </ul>
                <p className="font-semibold">
                  You are ultimately responsible for the accuracy and completeness of your tax return. We prepare returns based on the information you provide.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Fees and Payment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Tax preparation fees start at $100 and vary based on the complexity of your return, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Number of income sources and forms</li>
                  <li>Itemized deductions vs. standard deduction</li>
                  <li>Self-employment income and expenses</li>
                  <li>State tax return preparation (additional fee)</li>
                  <li>Amended returns (separate fee applies)</li>
                </ul>
                <p>
                  <span className="font-semibold">Payment Terms:</span> A quote will be provided before work begins. Payment is due upon completion of your return, before e-filing. We accept cash, check, and electronic payment methods.
                </p>
                <p>
                  <span className="font-semibold">Cancellation:</span> If you cancel after work has begun, you will be charged for the time and services rendered up to that point.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Engagement and Scope</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Our engagement is limited to the preparation of your income tax returns for the tax year(s) specified. This engagement does NOT include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Audit representation before the IRS (separate engagement required)</li>
                  <li>Tax planning services beyond the current return</li>
                  <li>Financial planning, investment advice, or legal counsel</li>
                  <li>Preparation of financial statements</li>
                  <li>Bookkeeping services (unless separately arranged)</li>
                </ul>
                <p>
                  If your return is selected for audit, we will provide limited guidance on the audit process. Full audit representation requires a separate engagement agreement and fee.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Professional Conduct</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Tiny Tax Lady adheres to the highest professional standards, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Compliance with IRS Circular 230 regulations governing tax practitioners</li>
                  <li>Adherence to federal and state tax laws and regulations</li>
                  <li>Continuing education to stay current with tax law changes</li>
                  <li>Ethical conduct in all client relationships</li>
                </ul>
                <p>
                  We will not prepare returns that we believe are fraudulent, contain false information, or violate tax laws. We reserve the right to decline or withdraw from any engagement that conflicts with professional standards.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Security and Confidentiality</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We take the security of your personal and financial information seriously. Your tax documents are:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Transmitted via encrypted secure channels</li>
                  <li>Stored using secure, password-protected systems</li>
                  <li>Accessed only by authorized personnel</li>
                  <li>Protected by physical and digital security measures</li>
                </ul>
                <p>
                  We maintain strict confidentiality of all client information in accordance with professional standards and applicable laws. For more details, please review our <Link to="/privacy" className="text-secondary hover:underline">Privacy Policy</Link>.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  While we exercise due care in preparing your tax returns, we cannot guarantee results or outcomes with tax authorities. Our liability is limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Re-preparation of your return if errors are due to our negligence (at no additional charge)</li>
                  <li>Refund of fees paid for the return in question</li>
                </ul>
                <p>
                  <span className="font-semibold">We are NOT liable for:</span>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Errors resulting from incomplete, inaccurate, or untimely information provided by you</li>
                  <li>IRS or state penalties and interest due to your failure to file or pay taxes on time</li>
                  <li>Tax liabilities arising from positions taken based on your instructions</li>
                  <li>Consequential, indirect, or punitive damages</li>
                  <li>Issues arising from returns not prepared by Tiny Tax Lady</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Amendments and Corrections</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  If you discover errors or receive additional documentation after your return has been filed, we can prepare an amended return (Form 1040-X). Amended returns require:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>A separate engagement and fee</li>
                  <li>Clear documentation of the changes needed</li>
                  <li>Your signature authorizing the amendment</li>
                </ul>
                <p>
                  If we discover an error due to our negligence, we will prepare the amended return at no charge.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Termination</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Either party may terminate this engagement at any time with written notice. Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You will be billed for services rendered through the date of termination</li>
                  <li>All client documents and work product will be returned to you</li>
                  <li>We will provide any work completed up to that point</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dispute Resolution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Any disputes arising from these Terms or our services shall be resolved through:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Good faith negotiation between the parties</li>
                  <li>Mediation if negotiation is unsuccessful</li>
                  <li>Binding arbitration or litigation in Kansas courts as a last resort</li>
                </ul>
                <p>
                  These Terms are governed by the laws of the State of Kansas.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Changes to Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We reserve the right to modify these Terms at any time. Changes will be posted on our website with an updated effective date. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  If you have questions about these Terms and Conditions, please contact us:
                </p>
                <div className="space-y-2 mt-4">
                  <p className="font-semibold">Tiny Tax Lady</p>
                  <p>Diane Hardesty</p>
                  <p>9330 Gillette St.</p>
                  <p>Lenexa, KS 66215</p>
                  <p>Phone: <a href="tel:913-406-7852" className="text-secondary hover:underline">913-406-7852</a></p>
                  <p>Fax: 913-271-4416</p>
                  <p>Email: <a href="mailto:Diane@TinyTaxLady.com" className="text-secondary hover:underline">Diane@TinyTaxLady.com</a></p>
                </div>
              </CardContent>
            </Card>

            <div className="text-center pt-8">
              <p className="text-sm text-muted-foreground">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Terms;
